/* global __SEARCH_CONTEXT__ */
const _NATURAL_LANGUAGE_QUERY_IDENTIFICATORS = ['what', 'where', 'why', 'find', 'search', 'who', 'how'];

export default class SearchAutocomplete {
    constructor(searchFormElement, searchInputElement, searchClearElement, useAI = false) {
        this.searchForm = searchFormElement;
        this.searchInput = searchInputElement;
        this.searchClear = searchClearElement;
        this.useAI = useAI;

        this.currentFocus = -1;
        this.suggestionsContainer = null;
        this.suggestionsAI = null;
    }

    initialize() {
        this.suggestionsContainer = this._createSuggestionsContainer(this.searchInput);

        this._initializeInputListener();
        this._initializeFocusListener();
        this._initializeClearListener();
        this._initializeKeyListener();
        this._initializeClickListener();
    }

    setAISuggestions(suggestions = []) {
        this.suggestionsAI = suggestions;
    }

    _debounce(func, timeout = 300) {
        let timer;
        return (...args) => {
            clearTimeout(timer);
            timer = setTimeout(() => {
                func.apply(this, args);
            }, timeout);
        };
    }

    _initializeInputListener = () => {
        this.searchInput.addEventListener(
            'input',
            this._debounce((e) => this._on_click(e)),
        );
    };

    _initializeClearListener = () => {
        this.searchClear.addEventListener('click', () => {
            this.searchInput.value = '';
            this._hideXButton();
            this.searchInput.focus();
        });
    };

    _initializeFocusListener = () => {
        this.searchInput.addEventListener('focus', (e) => this._on_click(e));
    };

    _on_click(e) {
        this._closeAllLists();
        const { value } = e.target;

        this.currentFocus = -1;

        if (value.toLowerCase().length === 0) {
            this._hideXButton();

            if (this.useAI) return;

            this._displayRecentQueries();
        } else {
            this._showXButton();
            const encodedValue = encodeURIComponent(value.toLowerCase());
            this._displaySuggestions(encodedValue);
        }
    }

    _initializeKeyListener = () => {
        this.searchInput.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowDown') {
                this.currentFocus++;
                this._addActive();
            } else if (e.key === 'ArrowUp') {
                this.currentFocus--;
                this._addActive();
            } else if (e.key === 'Enter') {
                e.preventDefault();

                if (this.currentFocus > -1) {
                    const suggestionItems = this._getSuggestionItems();
                    suggestionItems?.[this.currentFocus].click();
                } else {
                    this.searchForm.requestSubmit();
                }
            }
        });
    };

    _initializeClickListener = () => {
        document.addEventListener('click', (e) => this._closeAllLists(e.target));
    };

    _displayRecentQueries = () => {
        const { recentQueryURL } = __SEARCH_CONTEXT__;
        const recentQueriesEndpoint = `${recentQueryURL}`;

        $.get({ url: recentQueriesEndpoint }).then((data) => {
            const { recent_queries: recentQueries } = data;

            if (recentQueries.length) {
                recentQueries.forEach((query) => {
                    const suggestionItem = this._createSuggestion(query, '', 'fa-clock-o');
                    this.suggestionsContainer?.appendChild(suggestionItem);
                });

                this.suggestionsContainer?.classList.remove('hidden');

                if (this.useAI) {
                    this.suggestionsContainer?.querySelector('.autocomplete__items-title').classList.add('hidden');
                }
            }
        });
    };

    _mostProbablyAISearch = (val) => {
        const words = decodeURI(val).toLowerCase().trim().split(' ');

        return _NATURAL_LANGUAGE_QUERY_IDENTIFICATORS.indexOf(words[0]) > -1;
    };

    _retrieveSuggestions = (val) => {
        return new Promise((resolve) => {
            if (this.useAI && this.suggestionsAI) {
                if (this._mostProbablyAISearch(val)) {
                    resolve([]);
                    return;
                }

                resolve(this.suggestionsAI);
                return;
            }

            const { suggestionsQueryURL } = __SEARCH_CONTEXT__;
            const suggestionsEndpoint = `${suggestionsQueryURL}?prefix=${val}`;

            $.get({ url: suggestionsEndpoint }).then((data) => {
                const { suggestions } = data;

                resolve(suggestions);
            });
        });
    };

    _getSuggestionItems = () => {
        return this.suggestionsContainer?.querySelectorAll('.autocomplete__item');
    };

    _displaySuggestions = async (val) => {
        const suggestions = await this._retrieveSuggestions(val);

        if (suggestions.length) {
            suggestions.forEach((suggestion) => {
                const suggestionItem =
                    this.useAI && this.suggestionsAI
                        ? this._createAISuggestion(suggestion, decodeURIComponent(val))
                        : this._createSuggestion(suggestion, decodeURIComponent(val), 'fa-search');
                this.suggestionsContainer?.appendChild(suggestionItem);
            });

            this.suggestionsContainer?.classList.remove('hidden');

            if (this.useAI && this.suggestionsAI) {
                this.suggestionsContainer?.querySelector('.autocomplete__items-title').classList.remove('hidden');

                if (this.currentFocus < 0) {
                    this.currentFocus = 0;
                    this._addActive();
                }
            }
        }
    };

    _createSuggestionsContainer = (inputElement) => {
        const { id, parentNode } = inputElement;
        const suggestionsContainer = document.createElement('div');

        suggestionsContainer.setAttribute('id', this._formatAutocompleteListId(id));
        suggestionsContainer.classList.add('autocomplete__items', 'hidden');
        parentNode.appendChild(suggestionsContainer);

        if (this.useAI) {
            const aiSuggestionsTitleContainer = document.createElement('div');

            aiSuggestionsTitleContainer.innerText = 'AI SUGGESTED';
            aiSuggestionsTitleContainer.classList.add('autocomplete__items-title', 'hidden');
            suggestionsContainer.appendChild(aiSuggestionsTitleContainer);
        }

        return suggestionsContainer;
    };

    _createAISuggestion = (suggestion, val) => {
        const suggestionItem = document.createElement('div');
        suggestionItem.classList.add('autocomplete__item', 'autocomplete__item--ai');

        const suggestionItemIcon = document.createElement('i');
        suggestionItemIcon.setAttribute('class', `icon icon-chatbot autocomplete__item-icon`);
        suggestionItem.appendChild(suggestionItemIcon);

        const suggestionItemText = document.createElement('span');
        suggestionItemText.innerHTML = suggestion.replace('%s', `<em>${val}</em>`);

        suggestionItem.appendChild(suggestionItemText);
        suggestionItem.addEventListener('click', () => {
            this._closeAllLists();
            this.searchInput.dataset.userInput = val;
            this.searchInput.value = suggestion.replace('%s', val);
            this.searchForm.requestSubmit();
        });

        return suggestionItem;
    };

    _createSuggestion = (suggestion, val, iconClass) => {
        const suggestionItem = document.createElement('div');
        suggestionItem.classList.add('autocomplete__item');

        const suggestionItemIcon = document.createElement('span');
        suggestionItemIcon.setAttribute('class', `fa ${iconClass} autocomplete__item-icon`);
        suggestionItem.appendChild(suggestionItemIcon);

        const suggestionItemText = document.createElement('span');
        suggestionItemText.innerHTML = `<span style="color:#212529">${suggestion.substr(
            0,
            val.length,
        )}</span>${suggestion.substr(val.length)}`;
        suggestionItem.appendChild(suggestionItemText);
        suggestionItem.addEventListener('click', () => {
            this._closeAllLists();
            this.searchInput.value = suggestion;
            this.searchForm.requestSubmit();
        });

        return suggestionItem;
    };

    _formatAutocompleteListId = (id) => `${id}-autocomplete-list`;

    _addActive = () => {
        const suggestionItems = this._getSuggestionItems();

        if (!suggestionItems) {
            return;
        }

        [...suggestionItems].forEach((el) => el.classList.remove('active'));

        if (this.currentFocus >= suggestionItems.length) {
            this.currentFocus = 0;
        }
        if (this.currentFocus < 0) {
            this.currentFocus = suggestionItems.length - 1;
        }

        suggestionItems[this.currentFocus].classList.add('active');
    };

    _closeAllLists = (element) => {
        if (element === this.searchInput || !this.suggestionsContainer) {
            return;
        }

        const items = document.querySelectorAll(`#${this.suggestionsContainer.id} .autocomplete__item`);
        const listParent = items.length ? items[0].parentNode : null;

        for (let i = 0; i < items.length; i++) {
            if (element !== items[i]) {
                items[i].parentNode.removeChild(items[i]);
            }
        }

        listParent?.classList.add('hidden');
    };

    _showXButton() {
        this.searchClear.classList.remove('hidden');
    }

    _hideXButton() {
        this.searchClear.classList.add('hidden');
    }
}
